import * as React from "react";
import { useDropzone, FileRejection, FileError } from "react-dropzone";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { UploadCloud, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog";
import { useTranslation } from "react-i18next";

interface FileUploadProps {
  accept?: Record<string, string[]>;
  maxFiles?: number;
  maxSize?: number;
  onDrop: (acceptedFiles: File[]) => void;
  className?: string;
  value?: string | null;
  onChange?: (value: string | null) => void;
  onClear?: () => void;
  imagePreview?: boolean;
}

export function FileUpload({
  accept = {
    'image/png': ['.png'],
  },
  maxFiles = 1,
  maxSize = 2097152, // 2MB default limit
  onDrop,
  className,
  value,
  onChange,
  onClear,
  imagePreview = true,
}: FileUploadProps) {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    onDrop,
  });

  // Function to get the absolute URL for the image if it's a relative path
  const getImageUrl = (imagePath: string | null) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${import.meta.env.VITE_API_URL}${imagePath}`;
  };

  // Function to handle delete confirmation
  const handleDeleteConfirm = () => {
    if (onClear) onClear();
    if (onChange) onChange(null);
    setShowDeleteConfirm(false);
  };

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={cn(
          "border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center cursor-pointer transition-colors",
          isDragActive
            ? "border-primary bg-primary/10"
            : "border-gray-300 hover:border-primary/50 hover:bg-primary/5",
          className
        )}
      >
        <input {...getInputProps()} />
        <UploadCloud className="h-12 w-12 text-gray-400 mb-2" />
        <div className="text-center space-y-1">
          <p className="text-sm font-medium">
            {isDragActive ? "Drop the file here" : "Drag & drop or click to upload"}
          </p>
          <p className="text-xs text-gray-500">
            PNG up to {Math.round(maxSize / 1048576)}MB
          </p>
        </div>
      </div>

      {fileRejections.length > 0 && (
        <div className="text-sm text-red-500">
          {fileRejections.map(({ file, errors }: FileRejection) => (
            <div key={file.name}>
              {errors.map((e) => (
                <p key={e.code}>{e.message}</p>
              ))}
            </div>
          ))}
        </div>
      )}

      {imagePreview && value && (
        <div className="relative mt-2 rounded-md overflow-hidden border border-gray-200">
          <img 
            src={getImageUrl(value)} 
            alt="Logo preview" 
            className="max-h-24 object-contain mx-auto"
          />
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-1 right-1 rounded-full h-6 w-6"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setShowDeleteConfirm(true);
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('account.deleteLogo')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('account.deleteLogoConfirmation')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 